import React, { useState } from 'react';
import PropTypes from 'prop-types';
import SearchInput from './filters/SearchInput';
import SelectFilter from './filters/SelectFilter';
import DateRangeFilter from './filters/DateRangeFilter';
import { useLanguage } from '../../../../hooks/useLanguage';
import { common as frCommon } from '../../../../locales/fr/common';
import { common as esCommon } from '../../../../locales/es/common';
import { common as arCommon } from '../../../../locales/ar/common';

const FilterSection = ({ primaryFilters = [], secondaryFilters = [], onAdd }) => {
  const [showSecondary, setShowSecondary] = useState(false);
  const { lang } = useLanguage();
  const translations = {
    fr: frCommon,
    es: esCommon,
    ar: arCommon
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[lang];
    for (const k of keys) {
      value = value[k];
    }
    return value;
  };

  const handlePrint = () => {
    window.print();
  };

  const renderFilter = (filter) => {
    switch (filter.type) {
      case 'search':
        return (
          <div key={filter.key} className="flex-1 min-w-[200px]">
            <SearchInput
              onChange={filter.onChange}
              placeholder={filter.placeholder}
              useAzerty={filter.useAzerty}
            />
          </div>
        );

      case 'select':
        return (
          <div key={filter.key} className="flex-1">
            <SelectFilter
              value={filter.value || ''}
              onChange={filter.onChange}
              options={filter.options || []}
              placeholder={filter.label}
            />
          </div>
        );

      case 'dateRange':
        return (
          <div key={filter.key} className="flex-1">
            <DateRangeFilter
              startDate={filter.startDate}
              endDate={filter.endDate}
              onStartDateChange={filter.onStartDateChange}
              onEndDateChange={filter.onEndDateChange}
            />
          </div>
        );

      case 'sort':
        return (
          <div key={filter.key} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => filter.onChange(filter.value)}
              className="text-gray-700 hover:text-gray-900"
              title={`${t('filters.sort.label')} ${filter.direction === 'desc' ? t('filters.sort.directions.desc') : t('filters.sort.directions.asc')}`}
            >
              <svg 
                className={`w-4 h-4 transform transition-transform duration-200 ${filter.direction === 'desc' ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
              </svg>
            </button>
            <SelectFilter
              value={filter.value || ''}
              onChange={filter.onChange}
              options={filter.options || []}
              placeholder={filter.label}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="mb-4 relative">
      {/* Bloc primaire - toujours visible */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between" dir="ltr">
          <div className="flex-1 flex gap-4">
            {primaryFilters.map(renderFilter)}
          </div>
          <div className="flex items-center gap-2">
            {/* Bouton d'impression */}
            <button
              onClick={handlePrint}
              className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
              title={t('actions.print')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
            </button>

            {/* Bouton d'export */}
            <button
              onClick={() => {}}
              className="p-2 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
              title={t('actions.export')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </button>

            {/* Bouton d'ajout */}
            <button
              onClick={onAdd}
              className="p-2 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
              title={t('actions.add')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>

            {/* Bouton de collapse */}
            {secondaryFilters.length > 0 && (
              <svg
                onClick={() => setShowSecondary(!showSecondary)}
                className={`h-5 w-5 text-gray-500 cursor-pointer transform ${showSecondary ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                title={showSecondary ? t('actions.collapse') : t('actions.expand')}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </div>
        </div>
      </div>

      {/* Bloc secondaire - flottant */}
      {showSecondary && secondaryFilters.length > 0 && (
        <div className="absolute z-20 w-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200">
          <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
            <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {secondaryFilters.map(renderFilter)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

FilterSection.propTypes = {
  primaryFilters: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['search', 'select', 'sort', 'dateRange']).isRequired,
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
      placeholder: PropTypes.string,
      value: PropTypes.any,
      direction: PropTypes.oneOf(['asc', 'desc']),
      onChange: PropTypes.func.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          label: PropTypes.string.isRequired
        })
      ),
      useAzerty: PropTypes.bool,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      onStartDateChange: PropTypes.func,
      onEndDateChange: PropTypes.func
    })
  ),
  secondaryFilters: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['search', 'select']).isRequired,
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
      placeholder: PropTypes.string,
      value: PropTypes.any,
      onChange: PropTypes.func.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          label: PropTypes.string.isRequired
        })
      )
    })
  ),
  onAdd: PropTypes.func
};

export default FilterSection; 